import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../lib/api';

function loadContacts() {
  return Promise.all([
    apiFetch('/users/contacts').catch(() => []),
    apiFetch('/users/google-contacts').catch(() => []),
  ]).then(([local, google]) => {
    const map = new Map();
    [...local, ...google].forEach(c => {
      if (c.email && !map.has(c.email.toLowerCase())) map.set(c.email.toLowerCase(), c);
    });
    return [...map.values()];
  }).catch(() => []);
}

export default function EmailSearch({ value, onChange, onSelect, placeholder, style }) {
  const [query,    setQuery]    = useState(value || '');
  const [results,  setResults]  = useState([]);
  const [contacts, setContacts] = useState([]);
  const [show,     setShow]     = useState(false);
  const ref = useRef();

  useEffect(() => { loadContacts().then(setContacts); }, []);

  useEffect(() => { setQuery(value || ''); }, [value]);

  const search = (q) => {
    if (!q || q.length < 1) { setResults([]); return; }
    const lower = q.toLowerCase();
    const matches = contacts.filter(c =>
      c.email.toLowerCase().includes(lower) ||
      (c.name && c.name.toLowerCase().includes(lower))
    ).slice(0, 8);
    setResults(matches);
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    search(val);
    setShow(true);
  };

  const select = (contact) => {
    if (onSelect) {
      // onSelect mode: clear input after selection, just notify parent
      setQuery('');
      onSelect(contact.email);
    } else {
      setQuery(contact.email);
      onChange(contact.email);
    }
    setResults([]);
    setShow(false);
  };

  const srcIcon = { user:'fa-user', team:'fa-users', distribution:'fa-envelope', google:'fa-brands fa-google' };

  return (
    <div style={{ position:'relative' }} ref={ref}>
      <input
        value={query}
        onChange={handleChange}
        onFocus={() => { setShow(true); search(query); }}
        onBlur={() => setTimeout(() => setShow(false), 150)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (onSelect && query.includes('@')) onSelect(query);
            e.currentTarget.blur();
          }
        }}
        placeholder={placeholder || 'Email address'}
        type="email"
        inputMode="email"
        enterKeyHint="done"
        autoComplete="email"
        style={style || { width:'100%', background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'10px 12px', fontSize:14, color:'var(--text)', outline:'none', boxSizing:'border-box' }}
      />
      {show && results.length > 0 && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, marginTop:4, zIndex:200, overflowY:'auto', maxHeight:280, boxShadow:'0 8px 24px rgba(0,0,0,0.3)' }}>
          {results.map((c, i) => (
            <div key={i} onMouseDown={() => select(c)}
              style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', cursor:'pointer', borderBottom: i < results.length-1 ? '1px solid var(--border)' : 'none', background:'transparent' }}
              onMouseEnter={e => e.currentTarget.style.background='rgba(79,156,249,0.08)'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              <i className={srcIcon[c.source] || 'fa-solid fa-envelope'} style={{ fontSize:12, color:'var(--text3)' }}></i>
              <div style={{ flex:1, minWidth:0 }}>
                {c.name && <p style={{ fontSize:13, fontWeight:700, color:'var(--text)', margin:0 }}>{c.name}</p>}
                <p style={{ fontSize:12, color:'var(--text3)', margin:0 }}>{c.email}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
